import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { EntityType } from "types/common.types"
import type { CsvDownloadGroup, CsvSource, CsvSourceTelemetry } from "types/csv.types"
import useDevices from "helpers/hooks/useDevices"
import useSelectedTowers from "helpers/hooks/towers/useSelectedTowers"
import { getTelemetryWithUnit } from "helpers/utils/telemetries"
import useCsvDownloadGroups from "./useCsvDownloadGroups"

export interface UseCsvDownloadSourcesReturn {
  sources: CsvSource[]
  isLoading: boolean
}

const useCsvDownloadSources = (
  entity: EntityType,
  entityId: number,
): UseCsvDownloadSourcesReturn => {
  const { t } = useTranslation()
  const { groups, isLoading } = useCsvDownloadGroups(entity, entityId)

  const needsDevices = useMemo(
    () => groups.some((g) => g.entity === "device" && !g.geohash),
    [groups],
  )
  const { devsWRelsById } = useDevices({ skip: !needsDevices })

  const towerIds = useMemo(
    () => (entity === "tower" ? [entityId] : []),
    [entity, entityId],
  )
  const { towersById } = useSelectedTowers({ towerIds, skip: entity !== "tower" })

  const sources = useMemo(() => {
    const toTelemetries = (fields: string[] | undefined): CsvSourceTelemetry[] =>
      (fields ?? [])
        .map((apiName) => ({ apiName, label: getTelemetryWithUnit(apiName, t) }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

    const entityLabel = (e: EntityType): string => t(`entities.${e.toUpperCase()}`)

    return groups.flatMap((group: CsvDownloadGroup): CsvSource[] => {
      if (group.geohash) {
        const subtitle =
          group.entity === "device"
            ? t("csv_drawer.WEATHER_SUBTITLE_DEVICE")
            : t("csv_drawer.WEATHER_SUBTITLE_TOWER")
        return [
          {
            key: `weather:${group.geohash}:${group.measurement ?? ""}`,
            entity: group.entity,
            geohash: group.geohash,
            measurement: group.measurement,
            isWeather: true,
            displayName: t("csv_drawer.WEATHER_SERVICE"),
            subtitle,
            telemetries: toTelemetries(group.fields),
          },
        ]
      }

      const telemetries = toTelemetries(group.fields)
      return group.ids.map((id): CsvSource => {
        const device = group.entity === "device" ? devsWRelsById?.[id]?.device : undefined
        const tower = group.entity === "tower" ? towersById[id] : undefined
        const displayName = device?.name || tower?.name || entityLabel(group.entity)
        const subtitleId = device?.less_id ?? id
        return {
          key: `${group.entity}:${id}:${group.measurement ?? ""}`,
          entity: group.entity,
          id,
          measurement: group.measurement,
          isWeather: false,
          displayName,
          subtitle: t("csv_drawer.SOURCE_ID", { id: subtitleId }),
          telemetries,
        }
      })
    })
  }, [groups, devsWRelsById, towersById, t])

  return { sources, isLoading }
}

export default useCsvDownloadSources
